import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ExternalLink } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export default function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | post object

  const load = async () => {
    const { data } = await api.get("/blog");
    setPosts(data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este artículo? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/admin/blog/${id}`);
      toast.success("Artículo eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl text-white">Blog · Noticias fiscales</h2>
        <button
          onClick={() => setEditing("new")}
          data-testid="blog-new-btn"
          className="px-5 py-2.5 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#FDE047] transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Nuevo artículo
        </button>
      </div>

      <div className="bg-[#121214] border border-white/5 rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-white/5 text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-6 py-3 font-normal">Título</th>
              <th className="text-left px-6 py-3 font-normal">Categoría</th>
              <th className="text-left px-6 py-3 font-normal">Slug</th>
              <th className="text-left px-6 py-3 font-normal">Fecha</th>
              <th className="text-right px-6 py-3 font-normal">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {posts.map((p) => (
              <tr key={p.id} className="hover:bg-white/5" data-testid={`blog-row-${p.id}`}>
                <td className="px-6 py-4 text-white">{p.title}</td>
                <td className="px-6 py-4"><span className="text-xs px-2 py-1 rounded-full border border-[#D4AF37]/30 text-[#D4AF37]">{p.category}</span></td>
                <td className="px-6 py-4 font-mono text-xs text-zinc-500">{p.slug}</td>
                <td className="px-6 py-4 text-xs font-mono text-zinc-500">{new Date(p.created_at).toLocaleDateString("es-ES")}</td>
                <td className="px-6 py-4 text-right space-x-2">
                  <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="inline-block text-zinc-400 hover:text-[#D4AF37]" title="Ver">
                    <ExternalLink className="w-4 h-4 inline" />
                  </a>
                  <button onClick={() => setEditing(p)} data-testid={`blog-edit-${p.id}`} className="text-zinc-400 hover:text-white" title="Editar">
                    <Pencil className="w-4 h-4 inline" />
                  </button>
                  <button onClick={() => remove(p.id)} data-testid={`blog-delete-${p.id}`} className="text-zinc-400 hover:text-red-400" title="Eliminar">
                    <Trash2 className="w-4 h-4 inline" />
                  </button>
                </td>
              </tr>
            ))}
            {!posts.length && (
              <tr><td colSpan={5} className="px-6 py-16 text-center text-zinc-500">Aún no hay artículos. Crea el primero.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <BlogEditor
          post={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function BlogEditor({ post, onClose, onSaved }) {
  const isNew = !post;
  const [form, setForm] = useState({
    title: post?.title || "",
    slug: post?.slug || "",
    excerpt: post?.excerpt || "",
    content: "",
    category: post?.category || "Fiscal",
    cover_image: post?.cover_image || "",
  });
  const [loading, setLoading] = useState(false);
  const [slugTouched, setSlugTouched] = useState(!isNew);

  useEffect(() => {
    if (post) {
      // Load full content
      api.get(`/admin/blog/${post.id}`).then((r) => {
        setForm((f) => ({ ...f, content: r.data.content }));
      });
    }
  }, [post]);

  const setField = (k, v) => {
    if (k === "title" && !slugTouched) {
      setForm((f) => ({ ...f, title: v, slug: slugify(v) }));
    } else {
      setForm((f) => ({ ...f, [k]: v }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isNew) {
        await api.post("/admin/blog", form);
        toast.success("Artículo publicado");
      } else {
        await api.put(`/admin/blog/${post.id}`, form);
        toast.success("Artículo actualizado");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#121214] border border-white/10 rounded-2xl p-8 w-full max-w-3xl my-8" data-testid="blog-editor-modal">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-2xl text-white">{isNew ? "Nuevo artículo" : "Editar artículo"}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-zinc-400" /></button>
        </div>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-1.5 block">Título *</label>
            <input
              required
              value={form.title}
              onChange={(e) => setField("title", e.target.value)}
              data-testid="blog-title"
              className="w-full bg-transparent border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-1.5 block">Slug (URL) *</label>
              <input
                required
                value={form.slug}
                onChange={(e) => { setSlugTouched(true); setField("slug", slugify(e.target.value)); }}
                data-testid="blog-slug"
                className="w-full bg-transparent border border-white/10 rounded-lg px-4 py-2.5 text-white font-mono text-sm focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-1.5 block">Categoría</label>
              <select
                value={form.category}
                onChange={(e) => setField("category", e.target.value)}
                data-testid="blog-category"
                className="w-full bg-transparent border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-[#D4AF37] focus:outline-none"
              >
                {["Fiscal","Contable","Laboral","Mercantil","General"].map((c) => (
                  <option key={c} value={c} className="bg-[#121214]">{c}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-1.5 block">Imagen de portada (URL)</label>
            <input
              type="url"
              value={form.cover_image}
              onChange={(e) => setField("cover_image", e.target.value)}
              placeholder="https://images.unsplash.com/..."
              data-testid="blog-cover"
              className="w-full bg-transparent border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-1.5 block">Extracto *</label>
            <textarea
              required
              rows={2}
              value={form.excerpt}
              onChange={(e) => setField("excerpt", e.target.value)}
              data-testid="blog-excerpt"
              className="w-full bg-transparent border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-[#D4AF37] focus:outline-none resize-none"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-1.5 block">Contenido * <span className="text-zinc-600 normal-case tracking-normal">(párrafos separados por línea en blanco)</span></label>
            <textarea
              required
              rows={10}
              value={form.content}
              onChange={(e) => setField("content", e.target.value)}
              data-testid="blog-content"
              className="w-full bg-transparent border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-[#D4AF37] focus:outline-none resize-y font-mono text-sm leading-relaxed"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="blog-save"
            className="w-full py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#FDE047] transition-colors disabled:opacity-60"
          >
            {loading ? "Guardando…" : isNew ? "Publicar artículo" : "Guardar cambios"}
          </button>
        </form>
      </div>
    </div>
  );
}
